import React from "react";
import { ArrowRight, PlayCircle, Sparkles } from "lucide-react";
import { MARLEA } from "@/constants/testIds";
import { ChatbotMockup } from "./ChatbotMockup";
import { GradientCheck } from "./GradientCheck";

export const Hero = ({ onScrollDemo, onScrollHow }) => {
  const benefits = [
    "Beantwoordt vragen automatisch, ook buiten kantooruren",
    "Helpt bezoekers sneller vinden wat ze zoeken",
    "Verzamelt naam, telefoon, e-mail en aanvraaginfo",
  ];

  return (
    <section id="top" className="relative pt-28 md:pt-32 pb-10 md:pb-14 overflow-hidden">
      <div className="absolute inset-0 -z-10 pointer-events-none">
        <div
          className="glow-blob-1 absolute -top-20 -left-20 h-[420px] w-[420px] rounded-full blur-3xl opacity-25"
          style={{ background: "radial-gradient(circle, rgba(168,255,0,0.45), transparent 60%)" }}
        />
        <div
          className="glow-blob-2 absolute top-20 -right-20 h-[480px] w-[480px] rounded-full blur-3xl opacity-25"
          style={{ background: "radial-gradient(circle, rgba(0,239,255,0.45), transparent 60%)" }}
        />
        <div
          className="absolute inset-0 opacity-[0.05]"
          style={{
            backgroundImage:
              "linear-gradient(rgba(255,255,255,0.6) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.6) 1px, transparent 1px)",
            backgroundSize: "64px 64px",
            maskImage: "radial-gradient(ellipse at center, black 30%, transparent 75%)",
            WebkitMaskImage: "radial-gradient(ellipse at center, black 30%, transparent 75%)",
          }}
        />
      </div>

      <div className="max-w-7xl mx-auto px-5 md:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-[1.05fr_1fr] gap-8 lg:gap-12 items-center">
          <div>
            <div
              data-testid={MARLEA.heroBadge}
              className="inline-flex items-center gap-2 rounded-full px-3 py-1 glass border border-[#00FFB7]/25"
            >
              <span className="h-1.5 w-1.5 rounded-full bg-[#00FFB7] pulse-dot" />
              <span className="text-[10px] uppercase tracking-[0.22em] font-mono-display text-[#00FFB7]">
                24/7 AI-chatbot voor websites
              </span>
            </div>

            <h1
              data-testid={MARLEA.heroHeadline}
              className="font-display font-semibold text-white mt-4 tracking-tight leading-[1.05] text-[30px] sm:text-[44px] lg:text-[50px] xl:text-[54px]"
            >
              <span className="text-gradient-neon">AI-chatbot</span> voor uw website die{" "}
              <span className="text-gradient-neon">24/7 vragen beantwoordt</span>
            </h1>

            <p
              data-testid={MARLEA.heroSubheadline}
              className="mt-3 text-[15px] md:text-base text-[#A7A7A7] leading-relaxed max-w-xl"
            >
              Marlea AI helpt bedrijven met een slimme chatbot op hun website die
              bezoekers direct helpt, veelgestelde vragen opvangt en aanvragen
              verzamelt — ook buiten kantooruren.
            </p>

            <ul className="mt-4 space-y-2.5">
              {benefits.map((b, i) => (
                <li
                  key={i}
                  data-testid={MARLEA.heroBenefit(i)}
                  className="flex items-start gap-2.5"
                >
                  <GradientCheck size={18} className="mt-[3px]" />
                  <span className="text-white/90 text-[14px] md:text-[15px]">{b}</span>
                </li>
              ))}
            </ul>

            <div className="mt-5 flex flex-col sm:flex-row gap-3">
              <button
                data-testid={MARLEA.heroPrimaryCta}
                onClick={onScrollDemo}
                className="group inline-flex items-center justify-center gap-2 rounded-full bg-gradient-neon px-6 py-3.5 text-[14px] font-semibold text-[#0A0A1A] transition-transform duration-200 hover:scale-[1.03] glow-cyan"
              >
                <Sparkles className="h-4 w-4" />
                Vraag gratis demo aan
                <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5" />
              </button>

              <button
                data-testid={MARLEA.heroSecondaryCta}
                onClick={onScrollHow}
                className="group inline-flex items-center justify-center gap-2 rounded-full px-6 py-3.5 text-[14px] font-medium text-[#00EFFF] border border-[#00EFFF]/35 hover:bg-[#00EFFF]/10 transition-colors"
              >
                <PlayCircle className="h-4 w-4" />
                Bekijk hoe het werkt
              </button>
            </div>

            <p
              data-testid={MARLEA.heroTrust}
              className="mt-3 text-[12px] text-[#A7A7A7] max-w-lg"
            >
              Geen verplichting. Eerst zien hoe het werkt, daarna pas beslissen.
            </p>
          </div>

          <div className="relative">
            <ChatbotMockup />
          </div>
        </div>
      </div>
    </section>
  );
};

export default Hero;
